import { useEffect, useState } from 'react';
import { useCurrentProject } from '@material-bank/projects-utils';
import { getShouldTriggerProjectConfirmationDialog } from 'UILibrary';
import { OpenPreviewFilesDialogCustomEvent } from '../previewFiles.types';

type EventType = {
  detail: OpenPreviewFilesDialogCustomEvent;
};

export function usePreviewFilesDialog(
  onOpenCallback?: (event: EventType) => void,
) {
  const { currentProject } = useCurrentProject();
  const [isOpen, setIsOpen] = useState(false);

  const openPreviewFileDialog = () => {
    setIsOpen(true);
  };

  const closePreviewFileDialog = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  const handleOpen = async (event: EventType) => {
    const currentProjectId = currentProject?.externalId?.toString() ?? '';
    const shouldTriggerProjectConfirmationDialog =
      getShouldTriggerProjectConfirmationDialog();

    if (shouldTriggerProjectConfirmationDialog) {
      window.dispatchEvent(
        new CustomEvent('open-project-selection-dialog', {
          detail: {
            currentProjectId,
            analyticsEventLocation: 'Get pricing',
            onCurrentProjectSave: () => {
              openPreviewFileDialog();
              onOpenCallback?.(event);
            },
          },
        }),
      );
    } else {
      openPreviewFileDialog();
      onOpenCallback?.(event);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fileIdParam = parseInt(urlParams.get('fileId') ?? '', 10);

    if (!isNaN(fileIdParam)) {
      handleOpen({ detail: { selectedFileId: fileIdParam } });
    }
  }, [window.location.href]);

  useEffect(() => {
    const listener = (
      event: CustomEvent<OpenPreviewFilesDialogCustomEvent>,
    ) => {
      handleOpen(event);
    };

    window.addEventListener('open-preview-files-dialog', listener);

    return () => {
      window.removeEventListener('open-preview-files-dialog', listener);
    };
  }, []);

  return { isOpen, openPreviewFileDialog, closePreviewFileDialog };
}
